/* eslint-disable react/sort-comp */
import React, { Component, Fragment } from 'react';
import propTypes from 'prop-types';
import classnames from 'classnames';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';

import { EDIT_STATUS_MODIFIED, EDIT_STATUS_NEW } from 'app/App/const';
import ConfirmationModal from 'app/ConfirmationModal/components/ConfirmationModal';
import DiscardButton from 'app/DiscardButton/container';
import PublishComponent from 'app/PublishComponent/container';
import { visitComponentPath } from 'app/Router/paths';
import sharedStyles from 'app/shared.css';
import {
  BOUND_PROPLOCATION_PATH,
  CONTENT_POOL_EXCEPTION_MESSAGE,
  DEFAULT_BOUND_VALUE,
  MODAL_MESSAGES
} from 'lib/const';
import { PUBLISHED, SAVED } from 'lib/versions';
import { isContentPoolPlacement, isValidComponent } from 'lib/validations';
import {
  AlertButton,
  DuplicateComponentNamesMessage,
  PrimaryButton,
  SecondaryButton
} from 'ui/Components';
import { UI_ADD_MASTER_COMPONENT_TO_PAGE, UI_ADD_NEW_COMPONENT_TO_PAGE } from 'utils/factory';

import styles from './ComponentButtonsList.css';

/**
 * the component buttons
 * * @param {Object} props props
 * @returns {React.Component} the component buttons
 */
export default class ComponentButtonsList extends Component {
  static propTypes = {
    apiResponse: propTypes.shape(),
    clearComponentApiResponse: propTypes.func.isRequired,
    component: propTypes.shape(),
    copyComponent: propTypes.func.isRequired,
    editStatus: propTypes.string.isRequired,
    failedValidations: propTypes.object.isRequired,
    isValidComponent: propTypes.func,
    isReadonly: propTypes.bool,
    modifyComponent: propTypes.func.isRequired,
    removeAddedComponentFromPage: propTypes.func.isRequired,
    saveComponent: propTypes.func.isRequired,
    setFailedValidations: propTypes.func.isRequired,
    unboundVisitComponent: propTypes.func.isRequired,
    unpublishComponent: propTypes.func.isRequired,
    visitComponent: propTypes.func.isRequired,
    contractMap: propTypes.object.isRequired
  };

  static defaultProps = {
    isValidComponent,
    apiResponse: undefined,
    component: {},
    isReadonly: true
  };

  constructor(props) {
    super(props);
    this.state = {
      buttonDisabled: false,
      confirmationModal: { isOpen: false }
    };

    this.canBypassFailedValidations = this.canBypassFailedValidations.bind(this);
    this.handleUnpublishYesClick = this.handleUnpublishYesClick.bind(this);
    this.hideConfirmationModal = this.hideConfirmationModal.bind(this);
    this.prepareFailedValidationModalBody = this.prepareFailedValidationModalBody.bind(this);
    this.removeChildComponents = this.removeChildComponents.bind(this);
    this.removeComponent = this.removeComponent.bind(this);
    this.setDefaultBound = this.setDefaultBound.bind(this);
    this.showFailedValidationsModal = this.showFailedValidationsModal.bind(this);
    this.showOverrideDraftConfirmationModal = this.showOverrideDraftConfirmationModal.bind(
      this
    );
    this.showUnpublishConfirmationModal = this.showUnpublishConfirmationModal.bind(this);
    this.validateAndSaveComponent = this.validateAndSaveComponent.bind(this);
  }

  static getDerivedStateFromProps() {
    return { buttonDisabled: false };
  }

  /**
   * hides modal and execute confirmation Yes click
   */
  handleUnpublishYesClick() {
    const { component, unpublishComponent } = this.props;
    this.hideConfirmationModal();
    unpublishComponent(component);
  }

  /**
   * remove added component and disable button to remove from page
   */
  removeComponent() {
    const { removeAddedComponentFromPage } = this.props;

    removeAddedComponentFromPage();
    this.setState({ buttonDisabled: true });
  }

  /**
   * shows a confirmation modal to override existing v2 version of component
   * @returns {object} state to show existing v2 version confirmation
   */
  showOverrideDraftConfirmationModal() {
    const { clearComponentApiResponse, component, saveComponent } = this.props;
    return {
      isOpen: true,
      yesButtonLabel: 'Save and Override',
      onYesClick: () => {
        clearComponentApiResponse();
        saveComponent({ force: true });
      },
      noButtonLabel: 'Show Previous Draft',
      onNoClick: () => {
        clearComponentApiResponse();
        window.open(visitComponentPath(component.Id, SAVED), '_blank');
      },
      okButtonLabel: 'Cancel',
      onOkClick: clearComponentApiResponse,
      children:
        'There is already a saved version of this component. Please select one of the following options:'
    };
  }

  /**
   * shows a confirmation modal to unpublish the component
   */
  showUnpublishConfirmationModal() {
    this.setState({
      confirmationModal: {
        isOpen: true,
        onYesClick: this.handleUnpublishYesClick,
        onNoClick: this.hideConfirmationModal,
        children:
          'Are you sure you want to unpublish this component? Once unpublished this content will no longer appear on the site.'
      }
    });
  }

  /**
   * shows a modal to show failed validations
   * @param {object} failedValidations - Object containing failed validations
   * @returns {Component} return failed validation messages
   */
  prepareFailedValidationModalBody(failedValidations) {
    const children = (
      <ul className="p-4">
        {Object.keys(failedValidations).map((Id, index1) =>
          Object.keys(failedValidations[Id]).map(key => (
            <li key={`${Id}-${index1}-${key}`}>
              <span className={sharedStyles.validationMessage}>
                {Array.isArray(failedValidations[Id][key].message)
                  ? MODAL_MESSAGES.LIVE_TEXT_EDITOR_FAILED_MESSAGE
                  : failedValidations[Id][key].message}
              </span>
            </li>
          ))
        )}
      </ul>
    );

    return {
      isOpen: true,
      children
    };
  }

  /**
   * shows a modal to show failed validations
   * @param {object} failedValidations - Object containing failed validations
   */
  showFailedValidationsModal(failedValidations) {
    const modalBody = this.prepareFailedValidationModalBody(failedValidations);
    this.setState({
      confirmationModal: {
        ...modalBody,
        onOkClick: this.hideConfirmationModal
      }
    });
  }

  /**
   * hides the confirmation modal
   */
  hideConfirmationModal() {
    this.setState({ confirmationModal: { isOpen: false } });
  }

  /**
   * set a default value for `Payload.subcomponentsBound` if not found
   * @param {object} params - function params
   * @param {object} params.component - current component to save
   * @param {Function} params.modifyComponent - function to modify the component
   */
  setDefaultBound({ component, modifyComponent }) {
    if (
      isUndefined(component.Payload.subcomponentsBound) ||
      !component.Payload.subcomponentsBound
    ) {
      modifyComponent(DEFAULT_BOUND_VALUE, BOUND_PROPLOCATION_PATH);
    }
  }

  /**
   * removes child components
   * @param {object} params - function params
   * @param {object} params.component - current component to save
   * @param {Function} params.modifyComponent - function to modify the component
   */
  removeChildComponents({ component, modifyComponent }) {
    if (component.ChildLocation && get(component, component.ChildLocation).length) {
      modifyComponent([], component.ChildLocation);
    }
  }

  /**
   * determines if a component can bypass any failed validation messages before save
   * @param {object} params - function params
   * @param {object} params.component - current component to save
   * @param {object} params.failedValidations - failed validation messages
   * @param {Function} params.modifyComponent - function to modify the component
   * @returns {boolean} whether or not a component can bypass any failed validation messages before save
   */
  canBypassFailedValidations({ component, failedValidations, modifyComponent }) {
    if (
      isContentPoolPlacement({ component }) &&
      JSON.stringify(failedValidations).includes(CONTENT_POOL_EXCEPTION_MESSAGE)
    ) {
      this.setDefaultBound({ component, modifyComponent });
      this.removeChildComponents({ component, modifyComponent });
      return true;
    }
    return false;
  }

  /**
   * validates the component and makes save component call
   */
  validateAndSaveComponent() {
    const {
      component,
      modifyComponent,
      saveComponent,
      setFailedValidations,
      isValidComponent,
      contractMap
    } = this.props;
    const failedValidations = isValidComponent(component, contractMap);
    setFailedValidations(failedValidations);
    const hasFailedValidations = Object.keys(failedValidations).some(
      Id => Object.keys(failedValidations[Id]).length > 0
    );
    if (
      hasFailedValidations &&
      !this.canBypassFailedValidations({ component, failedValidations, modifyComponent })
    ) {
      this.showFailedValidationsModal(failedValidations);
    } else if (isContentPoolPlacement({ component })) {
      this.removeChildComponents({ component, modifyComponent });
      this.setDefaultBound({ component, modifyComponent });
      saveComponent();
    } else {
      saveComponent();
    }
  }

  /**
   * renders a unpublish component button
   * @returns {React.Component} button with click handler
   */
  renderConfirmationModal() {
    const { apiResponse, clearComponentApiResponse, unboundVisitComponent } = this.props;

    const savedVersionExists = get(apiResponse, 'savedVersionExists');
    const duplicateNamedComponents = get(apiResponse, 'duplicateNamedComponents');

    let { confirmationModal } = this.state;
    if (savedVersionExists) {
      confirmationModal = this.showOverrideDraftConfirmationModal();
    } else if (duplicateNamedComponents) {
      confirmationModal = this.prepareFailedValidationModalBody({
        allComponents: {
          ComponentNames: {
            message: (
              <DuplicateComponentNamesMessage
                duplicateNamedComponents={duplicateNamedComponents}
                visitComponent={unboundVisitComponent}
              />
            )
          }
        }
      });
      confirmationModal.onOkClick = clearComponentApiResponse;
    }
    const { children, ...restProps } = confirmationModal;

    return (
      <ConfirmationModal {...restProps} data-test-id="componentButtonsList-confirmationModal">
        {children}
      </ConfirmationModal>
    );
  }

  /**
   * renders a unpublish component button
   * @returns {React.Component} button with click handler
   */
  renderUnpublishButton() {
    const { component, isReadonly } = this.props;

    // we check if the component is dynamic and has a published version (version 1)
    if (component && component.Version === PUBLISHED && component.IsDynamic) {
      return (
        <Fragment>
          <AlertButton
            className={styles.unpublishButton}
            data-test-id="componentButtonsList-unpublishButton"
            disabled={isReadonly}
            onClick={this.showUnpublishConfirmationModal}
          >
            Unpublish
          </AlertButton>
        </Fragment>
      );
    }
    return <div />;
  }

  /**
   * renders a remove component from the page button
   * @param {string} uiAction - action taken on the component on UI
   * @param {boolean} disabled - disabled property for the button
   * @returns {React.Component} button with click handler
   */
  renderRemoveComponentFromPageButton(uiAction, disabled) {
    const { isReadonly } = this.props;
    if ([UI_ADD_MASTER_COMPONENT_TO_PAGE, UI_ADD_NEW_COMPONENT_TO_PAGE].includes(uiAction)) {
      return (
        <PrimaryButton
          className={styles.removeComponentButton}
          onClick={this.removeComponent}
          disabled={isReadonly || disabled}
          data-test-id="componentButtonsList-removeComponent"
        >
          Remove Component from Page
        </PrimaryButton>
      );
    }
    return null;
  }

  /**
   * renders a publish component button
   * @returns {React.Component} button with click handler
   */
  renderPublishButton() {
    const { component, isReadonly } = this.props;
    return (
      <PublishComponent
        component={component}
        isReadonly={isReadonly}
        data-test-id="componentButtonsList-publishButton"
      />
    );
  }

  /**
   * renders a discard button
   * @returns {React.Component} discard button component
   */
  renderDiscardButton() {
    const { component, editStatus, isReadonly, visitComponent } = this.props;
    return (
      <DiscardButton
        data-test-id="componentButtonsList-discardComponent"
        disabled={
          isReadonly || (editStatus !== EDIT_STATUS_MODIFIED && editStatus !== EDIT_STATUS_NEW)
        }
        id={component.Id}
        name={component.ComponentName}
        type="component"
        version={component.Version}
        visit={visitComponent}
      />
    );
  }

  /**
   * renders a save button based on component type
   * @returns {React.Component} button with click handler
   */
  renderSaveButton() {
    const { editStatus, isReadonly } = this.props;
    return (
      <span>
        <SecondaryButton
          className={styles.saveButton}
          onClick={() => this.validateAndSaveComponent()}
          data-test-id="componentButtonsList-saveComponent"
          disabled={
            isReadonly ||
            (editStatus !== EDIT_STATUS_MODIFIED && editStatus !== EDIT_STATUS_NEW)
          }
        >
          Save
        </SecondaryButton>
      </span>
    );
  }

  /**
   * renders a copy button based on component type
   * @returns {React.Component} button with click handler
   */
  renderCopyButton() {
    const { copyComponent, editStatus, isReadonly } = this.props;
    return (
      <span>
        <AlertButton
          className={styles.copyButton}
          onClick={copyComponent}
          data-test-id="componentButtonsList-copyComponent"
          disabled={
            isReadonly || editStatus === EDIT_STATUS_MODIFIED || editStatus === EDIT_STATUS_NEW
          }
        >
          Copy
        </AlertButton>
      </span>
    );
  }

  render() {
    const { component } = this.props;

    let styleFooter;
    let buttonWrapper;

    const addComponentToPage = [
      UI_ADD_MASTER_COMPONENT_TO_PAGE,
      UI_ADD_NEW_COMPONENT_TO_PAGE
    ].includes(component.uiAction);

    if (addComponentToPage) {
      styleFooter = styles.shortButtons;
      buttonWrapper = styles.shortButtonWrapper;
    } else {
      styleFooter = styles.tallButtons;
      buttonWrapper = styles.tallButtonWrapper;
    }
    const { buttonDisabled } = this.state;
    return (
      <div className={classnames(styles.buttons, styleFooter)}>
        <div className={`d-flex ${buttonWrapper}`}>
          {this.renderRemoveComponentFromPageButton(component.uiAction, buttonDisabled)}
          {this.renderCopyButton()}
          {this.renderSaveButton()}
          {this.renderDiscardButton()}
          {this.renderPublishButton()}
          {this.renderUnpublishButton()}
          {this.renderConfirmationModal()}
        </div>
      </div>
    );
  }
}
